import type { CanvasHistoryApi, HistoryContext, HistoryEntry, HistoryEntryInput, HistoryEvent } from "./canvasHistory.types";
import { COALESCE_MS, HISTORY_LIMIT } from "./canvasHistory.types";

type Snapshot = Pick<CanvasHistoryApi, "canUndo" | "canRedo" | "undoLabel" | "redoLabel" | "isBusy">;
export type CanvasHistoryStore = Omit<CanvasHistoryApi, keyof Snapshot> & { snapshot: () => Snapshot };

const compose = (a: HistoryEntryInput, b: HistoryEntryInput): HistoryEntryInput => {
  const { undo: undoA, redo: redoA } = a;
  const { undo: undoB, redo: redoB } = b;
  return {
    ...a,
    itemIds: Array.from(new Set([...(a.itemIds ?? []), ...(b.itemIds ?? [])])),
    undo: undoA && undoB ? async (ctx) => { await undoB(ctx); await undoA(ctx); } : null,
    redo: redoA && redoB ? async (ctx) => { await redoA(ctx); await redoB(ctx); } : null,
  };
};

export const createCanvasHistoryStore = (onChange: () => void = () => undefined, now: () => number = Date.now): CanvasHistoryStore => {
  let past: HistoryEntry[] = [];
  let future: HistoryEntry[] = [];
  let busy = false;
  let seq = 0;
  // Bumped by every clear, so a replay that was running meanwhile drops its result.
  let generation = 0;
  // A user action recorded while a replay ran: the redo stack can no longer be trusted.
  let interleaved = false;
  const aliases = new Map<string, string>();
  const listeners = new Set<(event: HistoryEvent) => void>();
  const flushers = new Set<() => Promise<void>>();
  const ctx: HistoryContext = {
    resolve: (id) => {
      let current = id;
      for (let hops = 0; hops < 50 && aliases.has(current); hops += 1) current = aliases.get(current) as string;
      return current;
    },
    alias: (oldId, newId) => { if (oldId !== newId) aliases.set(oldId, newId); },
  };
  const emit = (event: HistoryEvent) => { listeners.forEach((listener) => listener(event)); onChange(); };
  const clear = (reason?: string) => { generation += 1; past = []; future = []; aliases.clear(); emit({ kind: "cleared", reason: reason ?? null }); };
  const record = (input: HistoryEntryInput) => {
    if (busy) {
      // Inverses call services, never record, so this is a user action made during a replay.
      if (!input.undo || !input.redo) clear(input.label);
      else interleaved = true;
      return;
    }
    if (!input.undo || !input.redo) { clear(input.label); return; }
    future = [];
    const top = past[past.length - 1];
    const at = now();
    if (top && input.coalesceKey && top.coalesceKey === input.coalesceKey && at - top.at < COALESCE_MS) {
      const merged = top.merge ? top.merge(input) : compose(top, input);
      if (!merged.undo || !merged.redo) { clear(input.label); return; }
      past = [...past.slice(0, -1), { ...merged, merge: merged.merge ?? top.merge, id: top.id, at, coalesceKey: top.coalesceKey }];
    } else {
      seq += 1;
      past = [...past, { ...input, id: `h${seq}`, at }].slice(-HISTORY_LIMIT);
    }
    emit({ kind: "recorded", entry: past[past.length - 1] });
  };
  const replay = async (direction: "undo" | "redo") => {
    if (busy) return;
    for (const flush of Array.from(flushers)) { try { await flush(); } catch { /* a failed autosave must not block undo */ } } // a pending autosave records first
    if (busy) return; // another undo/redo started while flushing
    const entry = direction === "undo" ? past[past.length - 1] : future[future.length - 1];
    const step = entry && (direction === "undo" ? entry.undo : entry.redo);
    if (!entry || !step) return;
    busy = true;
    interleaved = false;
    const startedAt = generation;
    onChange();
    try {
      await step(ctx);
      busy = false;
      if (startedAt !== generation) { onChange(); return; } // cleared meanwhile: nothing to push back
      if (direction === "undo") { past = past.slice(0, -1); future = interleaved ? [] : [...future, entry]; }
      else { future = interleaved ? [] : future.slice(0, -1); past = [...past, entry]; }
      interleaved = false;
      emit({
        kind: direction === "undo" ? "undone" : "redone",
        entry,
        resolvedItemIds: (entry.itemIds ?? []).map(ctx.resolve),
      });
    } catch (error) {
      busy = false;
      if (startedAt !== generation) { onChange(); return; }
      emit({ kind: "failed", entry, error, direction });
    }
  };
  return {
    record,
    clear,
    undo: () => replay("undo"),
    redo: () => replay("redo"),
    registerFlusher: (flush) => { flushers.add(flush); return () => { flushers.delete(flush); }; },
    subscribe: (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    snapshot: () => ({
      canUndo: !busy && past.length > 0,
      canRedo: !busy && future.length > 0,
      undoLabel: past[past.length - 1]?.label ?? null,
      redoLabel: future[future.length - 1]?.label ?? null,
      isBusy: busy,
    }),
  };
};
